import {
  AbsoluteFill,
  Easing,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig
} from 'remotion';
import { BRAND } from './constants';

// Length of the checkmark path (slightly longer than the actual path so it's fully
// hidden when the dash offset equals it).
const CHECK_PATH_LENGTH = 50;

// The new Zenjob PRO wordmark (blue). Resolved via staticFile so it loads both in the
// in-app @remotion/player and in the Remotion renderer (from public/).
const LOGO = staticFile('assets/logo-primary.png');

/**
 * The CV-submit success animation as a Remotion composition: a calm, on-brand
 * confirmation on a TRANSPARENT background (it sits on the app's light page) — a blue
 * circle pops, a checkmark draws, a ring pulses out, then the Zenjob PRO wordmark rises
 * in. ~3.5s, wordless (the localized copy lives on the screen), no audio.
 */
export const CvSuccessVideo = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const fadeIn = interpolate(frame, [0, 10], [0, 1], {
    extrapolateRight: 'clamp'
  });

  const circleScale = spring({
    frame: Math.max(0, frame - 10),
    fps,
    config: { damping: 12, stiffness: 120, mass: 0.8 }
  });

  const checkDraw = interpolate(frame, [22, 42], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
    easing: Easing.bezier(0.22, 0.61, 0.36, 1)
  });
  const checkDashOffset = CHECK_PATH_LENGTH * (1 - checkDraw);

  const ringProgress = interpolate(frame, [16, 54], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp'
  });
  const ringScale = 1 + ringProgress * 0.8;
  const ringOpacity = interpolate(ringProgress, [0, 1], [0.4, 0]);

  const logoOpacity = interpolate(frame, [44, 66], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp'
  });
  const logoY = interpolate(frame, [44, 66], [24, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
    easing: Easing.bezier(0.22, 0.61, 0.36, 1)
  });

  return (
    <AbsoluteFill style={{ alignItems: 'center', justifyContent: 'center' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 56,
          opacity: fadeIn
        }}
      >
        <div
          style={{
            position: 'relative',
            width: 200,
            height: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div
            style={{
              position: 'absolute',
              width: 160,
              height: 160,
              borderRadius: '50%',
              border: `3px solid ${BRAND.blue}`,
              transform: `scale(${ringScale})`,
              opacity: ringOpacity
            }}
          />
          <div
            style={{
              width: 160,
              height: 160,
              borderRadius: '50%',
              backgroundColor: BRAND.blue,
              transform: `scale(${circleScale})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 12px 40px rgba(45, 49, 255, 0.28)'
            }}
          >
            <svg width={84} height={84} viewBox="0 0 52 52" fill="none">
              <path
                d="M14 27 L23 36 L39 18"
                stroke={BRAND.white}
                strokeWidth={6}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray={CHECK_PATH_LENGTH}
                strokeDashoffset={checkDashOffset}
              />
            </svg>
          </div>
        </div>
        <Img
          src={LOGO}
          style={{
            width: 200,
            height: 'auto',
            opacity: logoOpacity,
            transform: `translateY(${logoY}px)`
          }}
        />
      </div>
    </AbsoluteFill>
  );
};
